import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAdminPatients } from "@/portals/admin/context/AdminPatientsContext";
import type { PatientOut } from "@/portals/admin/api/adminPatientsApi";
import { cn } from "@/shared/lib/utils";
import { toastError } from "@/shared/lib/toast";
import { Pagination } from "@/shared/components/Pagination";
import { EmptyState } from "@/shared/components/EmptyState";
import { Input } from "@/shared/components/dialog";
import { Skeleton } from "antd";

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

const COLUMN_COUNT = 7;

function PatientRow({ patient }: { patient: PatientOut }) {
  const navigate = useNavigate();
  const open = () => navigate(`/admin/users/${encodeURIComponent(patient.id)}`);
  return (
    <tr
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter") open(); }}
      tabIndex={0}
      aria-label={`Open ${patient.name}`}
      className="cursor-pointer border-b border-[#E5E7EB] text-[#0A2947] last:border-0 hover:bg-[#FAFAFA]"
    >
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#180047] text-[11px] font-bold text-white">
            {getInitials(patient.name)}
          </div>
          <div>
            <p className="text-sm font-extrabold text-[#0A2947]">{patient.name}</p>
            <p className="text-xs text-[#4B5563]">{patient.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-xs text-[#4B5563]">{patient.phone || "—"}</td>
      <td className="px-4 py-4 text-xs text-[#4B5563]">{formatDate(patient.dob)}</td>
      <td className="px-4 py-4 text-xs capitalize text-[#4B5563]">{patient.gender || "—"}</td>
      <td className="px-4 py-4">
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-semibold",
            patient.is_verified ? "bg-[#ECFDF3] text-[#027A48]" : "bg-[#F3F4F6] text-[#6B7280]",
          )}
        >
          {patient.is_verified ? "Verified" : "Unverified"}
        </span>
      </td>
      <td className="px-4 py-4">
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-semibold",
            patient.is_disabled ? "bg-[#FEF3F2] text-[#E4002B]" : "bg-[#ECFDF3] text-[#027A48]",
          )}
        >
          {patient.is_disabled ? "Disabled" : "Active"}
        </span>
      </td>
      <td className="px-4 py-4 text-xs text-[#4B5563]">{formatDate(patient.created_at)}</td>
    </tr>
  );
}

export function UsersView() {
  const { patients, total, page, limit, search, loading, error, setPage, setSearch } = useAdminPatients();

  useEffect(() => {
    if (error) toastError(error);
  }, [error]);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-4 rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="relative max-w-md">
          <Icon icon="lucide:search" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="!w-full !rounded-xl !border-[#E5E7EB] !bg-[#F9FAFB] !py-2 !pl-9 !pr-3 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#FAFAFA]">
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Name</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Phone</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">DOB</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Gender</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Verified</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Status</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Registered</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td colSpan={COLUMN_COUNT} className="px-4 py-3">
                      <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
                    </td>
                  </tr>
                ))
              ) : patients.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-[#75787B]">
                    <EmptyState variant="compact" title="No patients found." className="!py-0" />
                  </td>
                </tr>
              ) : (
                patients.map((patient) => <PatientRow key={patient.id} patient={patient} />)
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} limit={limit} total={total} onChange={setPage} />
      </div>
    </div>
  );
}
